document.addEventListener("DOMContentLoaded", () => {
  const ANIMATE_CLASS = "animate";
  const ELEMENT_SELECTOR = ".lava, .lava-fade, .lava-left, .lava-right, .lava-scale";
  const CONTAINER_SELECTOR = ".lavaCont";
  const STAGGER_DELAY = 300;
  const DEFAULT_CONTAINER_OFFSET = 15; // % from bottom of viewport
  const DEFAULT_ELEMENT_OFFSET = 15; // % from bottom of viewport

  // If user prefers reduced motion, show all elements immediately
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
    document
      .querySelectorAll(ELEMENT_SELECTOR)
      .forEach((el) => {
        el.classList.add(ANIMATE_CLASS);
        el.style.opacity = "1";
      });
    return;
  }

  function toRootMargin(offset) {
    return `-${Math.max(0, offset - 5)}% 0px -${Math.max(0, offset)}% 0px`;
  }

  // Separate container-managed elements from standalone ones
  // Build scoped selector: each variant must be prefixed with container selector
  const containerScopedSelector = ELEMENT_SELECTOR.split(",")
    .map((s) => `${CONTAINER_SELECTOR} ${s.trim()}`)
    .join(", ");
  const containerElements = new Set(
    document.querySelectorAll(containerScopedSelector)
  );
  const containers = document.querySelectorAll(CONTAINER_SELECTOR);
  const standaloneElements = [
    ...document.querySelectorAll(ELEMENT_SELECTOR),
  ].filter((el) => !containerElements.has(el));

  if (!containers.length && !standaloneElements.length) return;


  // #region Containers

  // Track active timeouts per container to prevent stacking on rapid scroll
  const containerTimeouts = new Map();

  function clearPendingTimeouts(container) {
    const timeouts = containerTimeouts.get(container);
    if (timeouts) {
      timeouts.forEach(clearTimeout);
      containerTimeouts.delete(container);
    }
  }

  function staggerAnimateIn(container) {
    clearPendingTimeouts(container);
    const children = container.querySelectorAll(ELEMENT_SELECTOR);
    const timeouts = [];
    children.forEach((el, i) => {
      const id = setTimeout(
        () => el.classList.add(ANIMATE_CLASS),
        i * STAGGER_DELAY
      );
      timeouts.push(id);
    });
    containerTimeouts.set(container, timeouts);
  }

  function resetContainer(container) {
    clearPendingTimeouts(container);
    container
      .querySelectorAll(ELEMENT_SELECTOR)
      .forEach((el) => el.classList.remove(ANIMATE_CLASS));
  }

  // Container observer callback (shared across all offset-specific observers)
  // Use data-lava-reset to reset animation on scroll out
  function handleContainerIntersection(entries, observer) {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        staggerAnimateIn(entry.target);
        if (entry.target.dataset.lavaReset === undefined) {
          observer.unobserve(entry.target);
        }
      } else {
        resetContainer(entry.target);
      }
    });
  }

  // Group containers by offset, creating one observer per unique value
  // Use data-lava-offset to override the default per container
  const containersByOffset = new Map();

  containers.forEach((el) => {
    const parsed = parseFloat(el.dataset.lavaOffset);
    const offset = isNaN(parsed) ? DEFAULT_CONTAINER_OFFSET : parsed;
    if (!containersByOffset.has(offset)) {
      containersByOffset.set(offset, []);
    }
    containersByOffset.get(offset).push(el);
  });

  containersByOffset.forEach((elements, offset) => {
    const observer = new IntersectionObserver(handleContainerIntersection, {
      rootMargin: toRootMargin(offset),
    });
    elements.forEach((el) => observer.observe(el));
  });

  // #endregion Containers


  // #region Elements

  // Observe standalone elements — trigger when element enters viewport offset
  // Use data-lava-reset to reset animation on scroll out
  function handleElementIntersection(entries, observer) {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        entry.target.classList.add(ANIMATE_CLASS);
        if (entry.target.dataset.lavaReset === undefined) {
          observer.unobserve(entry.target);
        }
      } else {
        entry.target.classList.remove(ANIMATE_CLASS);
      }
    });
  }

  // Group standalone elements by offset, creating one observer per unique value
  // Use data-lava-offset to override the default per element
  const elementsByOffset = new Map();

  standaloneElements.forEach((el) => {
    const parsed = parseFloat(el.dataset.lavaOffset);
    const offset = isNaN(parsed) ? DEFAULT_ELEMENT_OFFSET : parsed;
    if (!elementsByOffset.has(offset)) {
      elementsByOffset.set(offset, []);
    }
    elementsByOffset.get(offset).push(el);
  });

  elementsByOffset.forEach((elements, offset) => {
    const observer = new IntersectionObserver(handleElementIntersection, {
      rootMargin: toRootMargin(offset),
    });
    elements.forEach((el) => observer.observe(el));
  });

  // #endregion Elements

  // #region Parallax

  const DEFAULT_PARALLAX_SPEED = -0.16;
  const rootStyles = getComputedStyle(document.documentElement);

  // Parse a data-parallax value into a pixel breakpoint and mode.
  // Syntax: "" (always), "lg" (min-width), "max-lg" (max-width),
  //         "1024" (min-width), "max-1024" (max-width).
  function parseParallaxValue(value) {
    if (!value) return { px: 0, mode: null };

    const isMax = value.startsWith("max-");
    const name = isMax ? value.slice(4) : value;

    // Try CSS custom property first (handles "2xl", "3xl", etc.)
    const cssVal = parseInt(rootStyles.getPropertyValue(`--breakpoint-${name}`), 10);
    const px = !isNaN(cssVal) ? cssVal : (parseInt(name, 10) || 0);

    if (px === 0) return { px: 0, mode: null };
    return { px, mode: isMax ? "max" : "min" };
  }

  // Group elements by breakpoint key
  const parallaxGroups = new Map(); // key → { mql, elements[] }
  const trackedParallaxElements = new Set();

  function registerParallaxElements(elements) {
    elements.forEach((el) => {
      if (trackedParallaxElements.has(el)) return;
      trackedParallaxElements.add(el);

      const { px, mode } = parseParallaxValue(el.dataset.parallax);
      const key = mode ? `${mode}-${px}` : "always";

      if (!parallaxGroups.has(key)) {
        let mql = null;
        if (mode === "min") mql = window.matchMedia(`(min-width: ${px}px)`);
        else if (mode === "max") mql = window.matchMedia(`(max-width: ${px - 1}px)`);

        parallaxGroups.set(key, { mql, elements: [] });

        // When a breakpoint stops matching, clear transforms
        if (mql) {
          mql.addEventListener("change", (e) => {
            if (!e.matches) {
              parallaxGroups.get(key).elements.forEach((el) => (el.style.transform = ""));
            }
          });
        }
      }
      parallaxGroups.get(key).elements.push(el);
    });
  }

  function updateParallax() {
    parallaxGroups.forEach(({ mql, elements }) => {
      if (mql && !mql.matches) return; // skip inactive breakpoint groups

      elements.forEach((el) => {
        const speed = parseFloat(el.dataset.parallaxSpeed) || DEFAULT_PARALLAX_SPEED;
        const rect = el.getBoundingClientRect();
        const center = rect.top + rect.height / 2;
        const offset = (window.innerHeight / 2 - center) * speed;
        el.style.transform = `translateY(${offset}px)`;
      });
    });
  }

  let parallaxTicking = false;
  function onParallaxScroll() {
    if (!parallaxTicking) {
      requestAnimationFrame(() => {
        updateParallax();
        parallaxTicking = false;
      });
      parallaxTicking = true;
    }
  }

  const initialParallaxElements = document.querySelectorAll("[data-parallax]");
  registerParallaxElements(initialParallaxElements);

  if (initialParallaxElements.length) {
    window.addEventListener("scroll", onParallaxScroll, { passive: true });
    window._parallaxScrollActive = true;
  }

  // #endregion Parallax
});